import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowDown } from 'lucide-react';
import { gsap, useGSAP } from '@/lib/gsap-config';
import { StudioSequence } from '@/features/landing/components/StudioSequence';
import './coming-soon.css';

const spark = 'M150 15C165 105 195 135 285 150C195 165 165 195 150 285C135 195 105 165 15 150C105 135 135 105 150 15Z';
const slide = 'M50 65H250Q270 65 270 85V215Q270 235 250 235H50Q30 235 30 215V85Q30 65 50 65Z';

export default function ComingSoonPage() {
  const { t, i18n } = useTranslation();
  const root = useRef<HTMLElement>(null);
  const path = useRef<SVGPathElement>(null);
  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.inOut' } });
      tl.from('.soon-enter', { y: 20, opacity: 0, duration: 0.8, stagger: 0.12 }, 0)
        .fromTo('.soon-morph', { morphSVG: spark }, { morphSVG: slide, duration: 2.1 }, 0.4)
        .fromTo('.soon-orbit', { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 2.4 }, 0.2)
        .fromTo('.soon-particle', { opacity: 1 }, { motionPath: { path: path.current!, align: path.current!, alignOrigin: [0.5, 0.5] }, duration: 2.4 }, 0.2)
        .to('.soon-particle', { opacity: 0, duration: 0.3 }, 2.6)
        .from('.soon-line', { drawSVG: '0% 0%', duration: 0.8, stagger: 0.1 }, 1.8);
      gsap.fromTo('.soon-reveal', { clipPath: 'circle(12% at 50% 0%)' }, {
        clipPath: 'circle(150% at 50% 0%)', ease: 'none',
        scrollTrigger: { trigger: '.soon-future', start: 'top 90%', end: 'center 55%', scrub: 0.7 },
      });
    });
    return () => media.revert();
  }, { scope: root, dependencies: [i18n.resolvedLanguage], revertOnUpdate: true });
  return <section className="coming-soon" ref={root}>
    <div className="soon-intro">
      <h1 className="soon-enter focus:outline-none" tabIndex={-1}>{t('soon.title')}<span>{t('soon.accent')}</span></h1>
      <p className="soon-description soon-enter">{t('soon.description')}</p>
      <Link className="soon-back soon-enter" to="/"><ArrowLeft size={15} aria-hidden="true" />{t('soon.back')}</Link>
      <div className="soon-sculpture">
        <StudioSequence variant="type" />
        <svg viewBox="0 0 600 360" fill="none" aria-hidden="true">
          <path ref={path} className="soon-orbit" d="M65 180C65 25 535 25 535 180C535 335 65 335 65 180Z" stroke="currentColor" strokeWidth=".7" />
          <g transform="translate(150 30)"><path className="soon-morph" d={slide} fill="currentColor" fillOpacity=".07" stroke="currentColor" strokeWidth="1.2" /><path className="soon-line" d="M70 115H165M70 139H140M70 190H140" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /><path className="soon-line" d="M180 180L198 145L214 160L234 120" stroke="currentColor" strokeWidth="2" /></g>
          <circle className="soon-particle" r="4" fill="currentColor" />
        </svg>
        <span className="soon-scroll"><ArrowDown size={13} aria-hidden="true" />{t('soon.scroll')}</span>
      </div>
    </div>
    <div className="soon-future"><div className="soon-reveal"><span>01 / {t('soon.next')}</span><h2>{t('soon.futureTitle')}</h2><p>{t('soon.futureDescription')}</p><div className="soon-chips"><span>{t('soon.idea')}</span><span>→</span><span>{t('soon.story')}</span><span>→</span><span>PowerPoint</span></div></div></div>
  </section>;
}
